/**
 * @fileoverview Gestão admin da Academy (super admin). 3 níveis: cursos → módulos
 * → aulas, com CRUD, reorder por setas ↑/↓ (envia o array completo de ids ao
 * endpoint reorder e refaz o fetch) e ConfirmationModal para deletes. Auto-guard
 * !user.super. Não é gated por enableAcademy.
 */

import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@material-ui/core";
import {
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import AcademyCourseModal from "../../components/AcademyCourseModal";
import AcademyModuleModal from "../../components/AcademyModuleModal";
import AcademyLessonModal from "../../components/AcademyLessonModal";

import { i18n } from "../../translate/i18n";
import useAcademy from "../../hooks/useAcademy";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1e293b",
  },
  buttonsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  addButton: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    height: 36,
    padding: "0 14px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    textTransform: "none",
    gap: 8,
    border: "none",
    boxShadow: "none",
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      boxShadow: "none",
    },
    "& svg": { flexShrink: 0 },
  },
  container: { padding: theme.spacing(2) },
  courseRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },
  spacer: { flex: 1 },
  moduleBlock: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  rowItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
  indent: { marginLeft: theme.spacing(2) },
  draft: { opacity: 0.7 },
}));

const AcademyManage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const {
    listCourses,
    showCourse,
    removeCourse,
    reorderCourses,
    removeModule,
    reorderModules,
    removeLesson,
    reorderLessons,
  } = useAcademy();

  const [courses, setCourses] = useState([]);
  const [trees, setTrees] = useState({});

  const [courseModal, setCourseModal] = useState({ open: false, course: null });
  const [moduleModal, setModuleModal] = useState({ open: false, module: null, courseId: null });
  const [lessonModal, setLessonModal] = useState({ open: false, lesson: null, moduleId: null });
  const [confirm, setConfirm] = useState({ open: false, title: "", onConfirm: null });

  // Auto-guard: só super admin
  useEffect(() => {
    if (user && !user.super) {
      toast.error(i18n.t("academy.toasts.noPermission"));
      history.push("/");
    }
  }, [user, history]);

  const loadCourses = useCallback(async () => {
    try {
      const data = await listCourses();
      setCourses(data || []);
    } catch (err) {
      toastError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTree = useCallback(
    async courseId => {
      try {
        const data = await showCourse(courseId);
        setTrees(prev => ({ ...prev, [courseId]: data }));
      } catch (err) {
        toastError(err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (user?.super) loadCourses();
  }, [user, loadCourses]);

  /* --------------------------- Reorder helpers --------------------------- */
  const reordered = (arr, index, dir) => {
    const next = [...arr];
    const target = index + dir;
    if (target < 0 || target >= next.length) return null;
    [next[index], next[target]] = [next[target], next[index]];
    return next.map(x => x.id);
  };

  const moveCourse = async (index, dir) => {
    const ids = reordered(courses, index, dir);
    if (!ids) return;
    try {
      await reorderCourses(ids);
      await loadCourses();
    } catch (err) {
      toastError(err);
    }
  };

  const moveModule = async (courseId, modules, index, dir) => {
    const ids = reordered(modules, index, dir);
    if (!ids) return;
    try {
      await reorderModules(courseId, ids);
      await loadTree(courseId);
    } catch (err) {
      toastError(err);
    }
  };

  const moveLesson = async (courseId, moduleId, lessons, index, dir) => {
    const ids = reordered(lessons, index, dir);
    if (!ids) return;
    try {
      await reorderLessons(moduleId, ids);
      await loadTree(courseId);
    } catch (err) {
      toastError(err);
    }
  };

  /* ------------------------------- Deletes ------------------------------- */
  const confirmDeleteCourse = course => {
    setConfirm({
      open: true,
      title: i18n.t("academy.manage.confirm.deleteCourse"),
      onConfirm: async () => {
        try {
          await removeCourse(course.id);
          setTrees(prev => {
            const next = { ...prev };
            delete next[course.id];
            return next;
          });
          await loadCourses();
        } catch (err) {
          toastError(err);
        }
      },
    });
  };

  const confirmDeleteModule = (courseId, module) => {
    setConfirm({
      open: true,
      title: i18n.t("academy.manage.confirm.deleteModule"),
      onConfirm: async () => {
        try {
          await removeModule(module.id);
          await loadTree(courseId);
        } catch (err) {
          toastError(err);
        }
      },
    });
  };

  const confirmDeleteLesson = (courseId, lesson) => {
    setConfirm({
      open: true,
      title: i18n.t("academy.manage.confirm.deleteLesson"),
      onConfirm: async () => {
        try {
          await removeLesson(lesson.id);
          await loadTree(courseId);
        } catch (err) {
          toastError(err);
        }
      },
    });
  };

  /* -------------------------------- Render ------------------------------- */
  const renderLessons = (courseId, module) => {
    const lessons = module.lessons || [];
    return lessons.map((lesson, idx) => (
      <div key={lesson.id} className={`${classes.rowItem} ${classes.indent}`}>
        <Typography variant="body2" style={{ flex: 1 }}>
          • {lesson.title}{" "}
          <Typography variant="caption" color="textSecondary">
            ({lesson.provider})
          </Typography>
        </Typography>
        <IconButton size="small" disabled={idx === 0} onClick={() => moveLesson(courseId, module.id, lessons, idx, -1)}>
          <ArrowUp size={16} />
        </IconButton>
        <IconButton size="small" disabled={idx === lessons.length - 1} onClick={() => moveLesson(courseId, module.id, lessons, idx, 1)}>
          <ArrowDown size={16} />
        </IconButton>
        <IconButton size="small" onClick={() => setLessonModal({ open: true, lesson, moduleId: module.id })}>
          <Pencil size={16} />
        </IconButton>
        <IconButton size="small" onClick={() => confirmDeleteLesson(courseId, lesson)}>
          <Trash2 size={16} />
        </IconButton>
      </div>
    ));
  };

  const renderModules = courseId => {
    const tree = trees[courseId];
    if (!tree) return <Typography variant="caption">...</Typography>;
    const modules = tree.modules || [];
    return (
      <div style={{ width: "100%" }}>
        <Button
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setModuleModal({ open: true, module: null, courseId })}
        >
          {i18n.t("academy.manage.buttons.addModule")}
        </Button>
        {modules.map((module, idx) => (
          <div key={module.id} className={classes.moduleBlock}>
            <div className={classes.rowItem}>
              <Typography variant="subtitle2" style={{ flex: 1 }}>
                {module.title}
              </Typography>
              <IconButton size="small" disabled={idx === 0} onClick={() => moveModule(courseId, modules, idx, -1)}>
                <ArrowUp size={16} />
              </IconButton>
              <IconButton size="small" disabled={idx === modules.length - 1} onClick={() => moveModule(courseId, modules, idx, 1)}>
                <ArrowDown size={16} />
              </IconButton>
              <IconButton size="small" onClick={() => setLessonModal({ open: true, lesson: null, moduleId: module.id })}>
                <Plus size={16} />
              </IconButton>
              <IconButton size="small" onClick={() => setModuleModal({ open: true, module, courseId })}>
                <Pencil size={16} />
              </IconButton>
              <IconButton size="small" onClick={() => confirmDeleteModule(courseId, module)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
            {renderLessons(courseId, module)}
          </div>
        ))}
      </div>
    );
  };

  if (user && !user.super) return null;

  return (
    <MainContainer className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          {i18n.t("academy.manage.title")}
        </Typography>
        <div className={classes.buttonsWrapper}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Plus size={15} strokeWidth={1.75} />}
            onClick={() => setCourseModal({ open: true, course: null })}
            className={classes.addButton}
          >
            {i18n.t("academy.manage.buttons.addCourse")}
          </Button>
        </div>
      </div>

      <Paper className={classes.container} variant="outlined">
        {courses.map((course, idx) => (
          <Accordion
            key={course.id}
            onChange={(_, expanded) => {
              if (expanded && !trees[course.id]) loadTree(course.id);
            }}
          >
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <div className={`${classes.courseRow} ${course.status ? "" : classes.draft}`}>
                <Typography variant="subtitle1">{course.title}</Typography>
                {!course.status && (
                  <Chip size="small" label={i18n.t("academy.manage.draft")} />
                )}
                <span className={classes.spacer} />
                <IconButton
                  size="small"
                  disabled={idx === 0}
                  onClick={e => {
                    e.stopPropagation();
                    moveCourse(idx, -1);
                  }}
                >
                  <ArrowUp size={16} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={idx === courses.length - 1}
                  onClick={e => {
                    e.stopPropagation();
                    moveCourse(idx, 1);
                  }}
                >
                  <ArrowDown size={16} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setCourseModal({ open: true, course });
                  }}
                >
                  <Pencil size={16} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    confirmDeleteCourse(course);
                  }}
                >
                  <Trash2 size={16} />
                </IconButton>
              </div>
            </AccordionSummary>
            <AccordionDetails>{renderModules(course.id)}</AccordionDetails>
          </Accordion>
        ))}
        {courses.length === 0 && (
          <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
            {i18n.t("academy.empty")}
          </Typography>
        )}
      </Paper>

      <AcademyCourseModal
        open={courseModal.open}
        course={courseModal.course}
        onClose={() => setCourseModal({ open: false, course: null })}
        reload={loadCourses}
      />
      <AcademyModuleModal
        open={moduleModal.open}
        module={moduleModal.module}
        courseId={moduleModal.courseId}
        onClose={() => setModuleModal({ open: false, module: null, courseId: null })}
        reload={() => moduleModal.courseId && loadTree(moduleModal.courseId)}
      />
      <AcademyLessonModal
        open={lessonModal.open}
        lesson={lessonModal.lesson}
        moduleId={lessonModal.moduleId}
        onClose={() => setLessonModal({ open: false, lesson: null, moduleId: null })}
        reload={() => {
          // descobre o courseId do módulo afetado e refaz o fetch do tree
          const courseId = Object.keys(trees).find(cid =>
            (trees[cid]?.modules || []).some(m => m.id === lessonModal.moduleId)
          );
          if (courseId) loadTree(Number(courseId));
        }}
      />
      <ConfirmationModal
        open={confirm.open}
        title={confirm.title}
        onClose={() => setConfirm({ open: false, title: "", onConfirm: null })}
        onConfirm={async () => {
          if (confirm.onConfirm) await confirm.onConfirm();
          setConfirm({ open: false, title: "", onConfirm: null });
        }}
      >
        {i18n.t("academy.manage.confirm.message")}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default AcademyManage;
